/* tslint:disable:forbid-import */
// Because this applies hotkeys outside of an observable component, we need to
// manually hook into mobx to support hot-swapping of keysets.
import { autorun } from 'mobx';
/* tslint:enable:forbid-import */
import { HotkeyCommand, Hotkeys } from 'owa-hotkeys/lib/types/HotkeyCommand';
import getKeysForKeyboardShortcutsMode from 'owa-hotkeys/lib/utils/getKeysForKeyboardShortcutsMode';
import shouldPreventCallback from 'owa-hotkeys/lib/utils/shouldPreventCallback';
import fixUpHotkeyCommand from 'owa-hotkeys/lib/utils/fixupHotkeyCommand';
import { lazyMousetrapImport } from 'owa-hotkeys/lib/utils/lazyMousetrapImport';
import * as React from 'react';

export interface GlobalHotkeyOptions {
    /**
     * Whether the event should stop propagating
     */
    stopPropagation?: boolean;
    /**
     * Whether to prevent the default browser action for the hotkey
     */
    preventDefault?: boolean;
    /**
     * Whether the hotkey should trigger, even if pressed while inside a text field
     */
    allowHotkeyOnTextFields?: boolean;
    /**
     * Function that returns whether the hotkey should be enabled.
     * If not provided, the hotkey will always be enabled until disposed
     */
    isEnabled?: () => boolean;
}

/**
 * Adds a hotkey handler to the root <body> element
 * @param command Set of swappable hotkeys to use as the trigger for the handler
 * @param handler A callback to issue when the provided hotkeys are pressed
 * @param options Additional options to control the hotkey
 * @returns A callback to dispose of the global hotkey
 */
export default async function addGlobalHotkey(
    command: HotkeyCommand | Hotkeys,
    handler: () => void,
    {
        allowHotkeyOnTextFields = false,
        isEnabled,
        stopPropagation = true,
        preventDefault = true,
    }: GlobalHotkeyOptions = {}
): Promise<() => void> {
    // Preprocess the keys based on platform, browser, and such

    // import is here to support React's server rendering as Mousetrap immediately
    // calls itself with window and it fails in Node environment
    const Mousetrap = await lazyMousetrapImport.import();
    const mousetrapInstance = new Mousetrap(document.body);

    const keys = getKeysForShortcutMode(command);

    const autorunDisposer = autorun(() => {
        bindGlobalHotkey(
            mousetrapInstance,
            keys,
            createHandlerMethod(
                allowHotkeyOnTextFields,
                isEnabled,
                stopPropagation,
                preventDefault,
                handler
            )
        );
    });

    return () => {
        mousetrapInstance.reset();
        autorunDisposer();
    };
}

export function useGlobalHotkey(
    command: HotkeyCommand | Hotkeys,
    handler: () => void,
    {
        allowHotkeyOnTextFields = false,
        isEnabled,
        stopPropagation = true,
        preventDefault = true,
    }: GlobalHotkeyOptions = {}
) {
    const handlerMethod = React.useCallback(
        createHandlerMethod(
            allowHotkeyOnTextFields,
            isEnabled,
            stopPropagation,
            preventDefault,
            handler
        ),
        [allowHotkeyOnTextFields, isEnabled, stopPropagation, preventDefault, handler]
    );
    const keys = React.useMemo(() => getKeysForKeyboardShortcutsMode(command), [command]);
    const Mousetrap = lazyMousetrapImport.tryImportForRender();

    React.useEffect((): (() => void) | void => {
        if (!Mousetrap || !keys) {
            return;
        }

        const mousetrapInstance = new Mousetrap(document.body);

        mousetrapInstance.bind(keys, handlerMethod);

        return () => mousetrapInstance.reset();
    }, [Mousetrap, keys, handler]);
}

function bindGlobalHotkey(
    mousetrapInstance: MousetrapInstance,
    keys: string | string[] | undefined,
    handler: (evt: KeyboardEvent) => void
) {
    mousetrapInstance.reset();

    if (keys) {
        mousetrapInstance.bind(keys, handler);
    }
}

function createHandlerMethod(
    allowHotkeyOnTextFields: boolean,
    isEnabled: (() => boolean) | undefined,
    stopPropagation: boolean,
    preventDefault: boolean,
    handler: () => void
) {
    return (evt: KeyboardEvent) => {
        if (shouldPreventCallback(evt, allowHotkeyOnTextFields) || (isEnabled && !isEnabled())) {
            return;
        }
        if (stopPropagation) {
            evt.stopPropagation();
        }
        if (preventDefault) {
            evt.preventDefault();
        }
        handler();
    };
}

function getKeysForShortcutMode(command: string | HotkeyCommand | string[]) {
    command = fixUpHotkeyCommand(command);
    const keys = getKeysForKeyboardShortcutsMode(command);
    return keys;
}
